/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */

'use client';

import { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useLanguage } from '@/providers/LanguageProvider';
import { iconMap } from '@/ux-common/src/lib/helper';
import { AppGraph, Component } from 'types/api-data';

import { useSearch } from '@/hooks/useSearch';

import { SearchInput } from '../common';
import ArchitectureServiceDialog from '../display-page/architecture/architecture-service-dialog';
import { Icons, System } from '../icons';

export const DisplayItems = ({ data }: { data?: AppGraph }) => {
  const { messages } = useLanguage();
  const langData = messages?.ApplicationDetailPage;

  // TIP: Custom hook for searching functionality.
  const { searchTerm, filteredItems, handlerSearchChange, handleClearSearch } =
    useSearch(data!, 'infra');

  const [openDialog, setOpenDialog] = useState(false);
  const [selectedUrl, setSelectedUrl] = useState<string | null>(null);

  const handleEndpointUrlClick = (url: string) => {
    setOpenDialog(true);
    setSelectedUrl(url);
  };

  const dataTransform = (data: AppGraph) => {
    const servicesData = data.components.filter(
      (item: Component) => item.domain === 'infra'
    );
    return servicesData;
  };

  return (
    <div className="w-full">
      <div className="mb-[11px] flex items-center justify-between">
        <h3 className="text-h2 text-body-primary">
          {messages?.General?.services}
        </h3>
        {data && dataTransform(data).length > 10 && (
          <SearchInput
            placeholder={messages?.General?.search_by_name}
            value={searchTerm}
            onChange={(e) => handlerSearchChange(e.target.value)}
            onClear={handleClearSearch}
          />
        )}
      </div>

      <div className="max-h-96 overflow-y-auto rounded-lg border border-line-secondary bg-canvas-background-primary px-2 py-3">
        {data && dataTransform(data).length > 0 ? (
          <>
            {filteredItems.length > 0 ? (
              <div className="text-body-primary">
                {filteredItems.map((item: Component) => (
                  <div
                    className="flex items-center gap-2.5 p-2.5"
                    key={item.display_name}
                  >
                    <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-white text-black">
                      <Image
                        className="size-5"
                        src={iconMap[item.type] ?? iconMap['default']}
                        alt="Logo"
                        width={100}
                        height={100}
                      />
                    </span>
                    <div className="flex flex-col">
                      <Link
                        href={{
                          pathname: '/services',
                          query: {
                            search: encodeURIComponent(item.display_name),
                          },
                        }}
                        className="group"
                      >
                        <span className="text-body-sm font-normal text-form-input-disabled">
                          {item.type}
                        </span>
                        <h4 className="text-h4 font-medium group-hover:underline group-hover:underline-offset-2">
                          {item.display_name}
                        </h4>
                      </Link>

                      {item?.properties?.component_url && (
                        <span
                          onClick={() => {
                            const endpointUrl = item.properties?.component_url;
                            if (endpointUrl) {
                              handleEndpointUrlClick(endpointUrl);
                            }
                          }}
                          className="flex cursor-pointer items-center gap-2 text-sm text-ocean hover:underline hover:underline-offset-2"
                        >
                          {messages?.Architecture.view_endpoint}
                          {Icons.PopOut('size-4 text-ocean')}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              // No matches found message
              <div className="flex flex-col items-center rounded-md p-4 text-white">
                {System.InfoIcon('size-6 text-form-input-disabled')}
                <p className="mb-3 mt-1 text-center text-xs">
                  {searchTerm
                    ? 'No services found.'
                    : langData?.you_dont_have_any_services_added}
                </p>
              </div>
            )}
          </>
        ) : (
          <div className="flex flex-col items-center rounded-md p-4 text-white">
            {System.InfoIcon('size-6 text-form-input-disabled')}
            <p className="mb-3 mt-1 text-center text-xs">
              {langData?.you_dont_have_any_services_added}
            </p>
          </div>
        )}
      </div>
      {selectedUrl && (
        <ArchitectureServiceDialog
          endpointUrl={selectedUrl}
          openDialog={openDialog}
          setOpenDialog={setOpenDialog}
        />
      )}
    </div>
  );
};
